import React, {useEffect, useState} from "react";
import {postData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import {updateDocumentTitle} from "../../Utils/Common";
import PageLayout from "../Layout/PageLayout";
import {Button, Card, DataTable, StatusBadge, FormField, Select, TextInput, Modal} from "../UI";

const OUTCOMES = ["", "SUCCESS", "FAILED"];
const STAGES = ["", "PREVALIDATION", "AREA_UNAVAILABLE", "BILLING", "SCHEDULE", "ROUTE", "WORKING_HOURS", "NO_RATES", "AREA_SETUP", "PERSIST", "CONFIRM", "OTHER"];
const LIMIT = 50;

const RequestLogList = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [areaList, setAreaList] = useState([]);
    const [filters, setFilters] = useState({outcome: "", failure_stage: "", area_id: "", source: "", mode: "", q: ""});
    const [detail, setDetail] = useState(null);

    const load = (nextOffset = offset) => {
        const data = new FormData();
        Object.entries(filters).forEach(([k, v]) => v && data.append(k, v));
        data.append("limit", LIMIT);
        data.append("offset", nextOffset);
        setIsLoading(true);
        postData(Endpoints.REQUEST_LOG_LIST, data)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                if (res.success === 1 && res.data) {
                    setRows(res.data.logs || []);
                    setTotal(res.data.total || 0);
                }
            })
            .catch(e => { setIsLoading(false); console.log(e); });
    };

    const loadAreas = () => {
        postData(Endpoints.AREA_LIST, new FormData())
            .then(res => res.json())
            .then(res => {
                if (res.success === 1) {
                    setAreaList(res.data || []);
                }
            })
            .catch(e => console.log(e));
    };

    useEffect(() => {
        updateDocumentTitle("Request Logs");
        load(0);
        loadAreas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyJson = (row) => {
        const data = new FormData();
        data.append("id", row.id);
        postData(Endpoints.REQUEST_LOG_DETAIL, data)
            .then(res => res.json())
            .then(res => {
                if (res.success === 1 && res.data) {
                    navigator.clipboard.writeText(JSON.stringify(res.data, null, 2));
                }
            })
            .catch(e => console.log(e));
    };

    const openDetail = (row) => {
        const data = new FormData();
        data.append("id", row.id);
        postData(Endpoints.REQUEST_LOG_DETAIL, data)
            .then(res => res.json())
            .then(res => { if (res.success === 1) setDetail(res.data); })
            .catch(e => console.log(e));
    };

    const columns = [
        {name: 'Time', selector: 'created_at', cell: row => <span>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</span>},
        {name: 'From', cell: row => <span>{row.source}{row.mode ? ` · ${row.mode}` : ''}</span>},
        {name: 'Outcome', cell: row => <StatusBadge tone={row.outcome === "SUCCESS" ? "success" : "danger"}>{row.outcome}</StatusBadge>},
        {name: 'Stage', selector: 'failure_stage', cell: row => <span>{row.failure_stage || '—'}</span>},
        {name: 'Area', selector: 'area_title', cell: row => <span>{row.area_title || '—'}</span>},
        {name: 'Code', selector: 'request_code', cell: row => <span>{row.request_code || '—'}</span>},
        {name: 'Reason', selector: 'failure_reason', cell: row => <span title={row.failure_reason}>{(row.failure_reason || '').slice(0, 60)}</span>},
        {name: '', cell: row => <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); copyJson(row); }}>Copy JSON</Button>},
    ];

    const setF = (k, v) => setFilters(prev => ({...prev, [k]: v}));

    return (
        <PageLayout title="Request Logs">
            <Card style={{marginBottom: 12}}>
                <div style={{display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end"}}>
                    <FormField label="Outcome" flush>
                        <Select value={filters.outcome} onChange={e => setF("outcome", e.target.value)}>
                            {OUTCOMES.map(o => <option key={o} value={o}>{o || "All"}</option>)}
                        </Select>
                    </FormField>
                    <FormField label="Stage" flush>
                        <Select value={filters.failure_stage} onChange={e => setF("failure_stage", e.target.value)}>
                            {STAGES.map(s => <option key={s} value={s}>{s || "All"}</option>)}
                        </Select>
                    </FormField>
                    <FormField label="Area" flush>
                        <Select value={filters.area_id} onChange={e => setF("area_id", e.target.value)}>
                            <option value="">All</option>
                            {areaList.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                        </Select>
                    </FormField>
                    <FormField label="Source" flush>
                        <Select value={filters.source} onChange={e => setF("source", e.target.value)}>
                            {["", "app", "api"].map(s => <option key={s} value={s}>{s || "All"}</option>)}
                        </Select>
                    </FormField>
                    <FormField label="Mode" flush>
                        <Select value={filters.mode} onChange={e => setF("mode", e.target.value)}>
                            {["", "live", "test"].map(s => <option key={s} value={s}>{s || "All"}</option>)}
                        </Select>
                    </FormField>
                    <FormField label="Search" flush>
                        <TextInput placeholder="code / ref / phone" value={filters.q} onChange={e => setF("q", e.target.value)}/>
                    </FormField>
                    <Button onClick={() => { setOffset(0); load(0); }}>Apply</Button>
                </div>
            </Card>

            <Card padded={false}>
                <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={isLoading}
                    noHeader
                    pointerOnHover
                    onRowClicked={openDetail}
                />
            </Card>

            <div style={{display: "flex", gap: 12, alignItems: "center", marginTop: 12}}>
                <Button variant="outline" disabled={offset === 0} onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o); }}>Prev</Button>
                <span>{total === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
                <Button variant="outline" disabled={offset + LIMIT >= total} onClick={() => { const o = offset + LIMIT; setOffset(o); load(o); }}>Next</Button>
            </div>

            <Modal
                open={!!detail}
                onClose={() => setDetail(null)}
                title={detail ? `Request log ${detail.id}` : "Request log"}
                maxWidth="md"
                actions={
                    <Button size="sm" onClick={() => navigator.clipboard.writeText(JSON.stringify(detail, null, 2))}>
                        Copy full JSON
                    </Button>
                }
            >
                <pre style={{maxHeight: 400, overflow: "auto", background: "#f6f6f6", padding: 12, whiteSpace: "pre-wrap"}}>
                    {detail ? JSON.stringify(detail, null, 2) : ""}
                </pre>
            </Modal>
        </PageLayout>
    );
};

export default RequestLogList;
